import { Stack, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ProjectOverviewDto, ProjectValueDto } from "../../autogen/bff-api";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { contractRegisterLinkFromProject } from "../contracts/view-all/FromProjectLine";
import { OverviewBar } from "./OverviewBar";
import { periodText, projectName } from "./projectFigures";

const VALUE_CURRENCY = "NOK";

export const ProjectValueCharts = ({ overview }: { overview: ProjectOverviewDto }) => (
  <Stack spacing="4">
    <ValuePerProject overview={overview} />
    <ProjectsPerType overview={overview} />
  </Stack>
);

const ValuePerProject = ({ overview }: { overview: ProjectOverviewDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const ranking = overview.valuePerProject;
  const money = (value: number) => displaySum({ value: Math.round(value), currency: ranking?.currency ?? VALUE_CURRENCY });
  const largest = Math.max(1, ...(ranking?.top ?? []).map((bar) => bar.value), ranking?.others?.value ?? 0);
  const link = (bar: ProjectValueDto) =>
    bar.visible ? contractRegisterLinkFromProject(bar.id, { projectIds: [bar.id, ...bar.subprojectIds] }) : null;

  return (
    <ChartBox heading={t("Contract value per project")}>
      {!ranking ? (
        <Text fontSize="sm" color="smMuted">
          {t("You do not have access to the contracts.")}
        </Text>
      ) : ranking.top.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No contract in the projects has a total price in {{currency}} yet.", { currency: ranking.currency })}
        </Text>
      ) : (
        <Stack spacing="1">
          {ranking.top.map((bar) => (
            <OverviewBar
              key={bar.id}
              label={projectName(t, bar)}
              width={(bar.value / largest) * 100}
              shown={money(bar.value)}
              details={[
                t("{{count}} contracts", { count: bar.contracts }),
                t("Value in the project: {{value}}", { value: money(bar.inProject) }),
                ...(bar.subprojectIds.length > 0
                  ? [t("Value in the subprojects: {{value}}", { value: money(bar.inSubprojects) })]
                  : []),
                t("Period: {{period}}", { period: periodText(bar, displayDate) }),
              ]}
              link={link(bar)}
              testId={`value-${bar.id}`}
            />
          ))}
          {ranking.others && (
            <OverviewBar
              label={String(t("Others"))}
              width={(ranking.others.value / largest) * 100}
              shown={money(ranking.others.value)}
              details={[
                t("{{count}} projects", { count: ranking.others.projects }),
                t("{{count}} contracts", { count: ranking.others.contracts }),
              ]}
              link={null}
              testId="value-others"
            />
          )}
        </Stack>
      )}
      {ranking && (
        <Stack spacing="1" fontSize="sm" color="smMuted">
          <Text>{t("Subprojects are counted in their main project.")}</Text>
          {ranking.contractsNotInBars > 0 && (
            <Text>
              {t("{{count}} contracts without a total price in {{currency}} are not in the bars.", {
                count: ranking.contractsNotInBars,
                currency: ranking.currency,
              })}
            </Text>
          )}
        </Stack>
      )}
    </ChartBox>
  );
};

const ProjectsPerType = ({ overview }: { overview: ProjectOverviewDto }) => {
  const { t } = useTranslation();
  const types = overview.perProjectType;
  const currency = overview.valuePerProject?.currency ?? VALUE_CURRENCY;
  const largest = Math.max(1, ...types.map((type) => type.ongoing));
  return (
    <ChartBox heading={t("Per project type")}>
      {types.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No ongoing projects")}
        </Text>
      ) : (
        <Stack spacing="1">
          {types.map((type) => (
            <OverviewBar
              key={type.projectTypeId ?? "none"}
              label={String(type.projectType ? t(type.projectType) : t("No project type"))}
              width={(type.ongoing / largest) * 100}
              shown={formatNumber(type.ongoing)}
              details={[
                t("{{count}} ongoing projects", { count: type.ongoing }),
                ...(type.contractValue == null || type.contracts == null
                  ? [t("You do not have access to the contracts.")]
                  : [
                      t("Contract value: {{value}}", {
                        value: displaySum({ value: Math.round(type.contractValue), currency }),
                      }),
                      t("{{count}} contracts", { count: type.contracts }),
                    ]),
              ]}
              link={null}
              testId={`type-${type.projectTypeId ?? "none"}`}
            />
          ))}
        </Stack>
      )}
    </ChartBox>
  );
};

const ChartBox = ({ heading, children }: { heading: string; children: ReactNode }) => (
  <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
    <Text as="h2" fontWeight="semibold" color="smText">
      {heading}
    </Text>
    {children}
  </Stack>
);
